import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../service/auth.service';
import { useAuth } from '../contexts/AuthContext';
import { Card, Typography, Space, Button, Input, Alert, Spin } from 'antd';
import { ArrowLeftOutlined, LockOutlined } from '@ant-design/icons';
import { useTheme } from '../components/ThemeProvider';

const { Title, Text } = Typography;

const ChangePasswordPage: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!token) {
      navigate('/login');
    }

    // Redirect to profile if user is not using local auth
    if (user && user.authProvider && user.authProvider !== 'local') {
      navigate('/profile');
    }
  }, [token, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!currentPassword) {
      setError('Please enter your current password');
      return;
    }

    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Call the change password API
      await AuthService.changePassword(currentPassword, newPassword);

      setSuccess(true);

      // Redirect to profile page after 3 seconds
      setTimeout(() => {
        navigate('/profile');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !user) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        backgroundColor: isDark ? '#141414' : '#fff',
        color: isDark ? '#fff' : '#000'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{
      padding: '24px',
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: isDark ? '#141414' : '#fff',
      color: isDark ? '#fff' : '#000',
      minHeight: '100vh'
    }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Space align="center">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/profile')}
            type="text"
            style={{ color: isDark ? '#fff' : '#000' }}
          >
            Back to Profile
          </Button>
          <Title level={2} style={{
            margin: 0,
            color: isDark ? '#fff' : '#000'
          }}>
            Change Password
          </Title>
        </Space>

        {/* Change Password Card */}
        <Card
          style={{
            backgroundColor: isDark ? '#1f1f1f' : '#fff',
            borderColor: isDark ? '#434343' : '#d9d9d9'
          }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {error && <Alert message={error} type="error" showIcon />}

            {success ? (
              <Alert
                message="Success"
                description="Your password has been changed successfully! You will be redirected to the profile page."
                type="success"
                showIcon
              />
            ) : (
              <form onSubmit={handleSubmit}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong style={{ color: isDark ? '#fff' : '#000' }}>
                      Current Password
                    </Text>
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter your current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Text strong style={{ color: isDark ? '#fff' : '#000' }}>
                      New Password
                    </Text>
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Text strong style={{ color: isDark ? '#fff' : '#000' }}>
                      Confirm New Password
                    </Text>
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{
                      backgroundColor: isDark ? '#1890ff' : '#1890ff',
                      borderColor: isDark ? '#1890ff' : '#1890ff'
                    }}
                    block
                  >
                    Change Password
                  </Button>
                </Space>
              </form>
            )}
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default ChangePasswordPage;
